/**
 * Datei-Identitäts-Helfer (P1.4 aus Plan_Improvements.md).
 *
 * Eine **einzige** Quelle für Namens- und Inhalts-Identität von Dateien, damit die Logik nicht
 * über mehrere Stores/Composables dupliziert wird (vormals u. a. `griffschriftName` doppelt in
 * `useFileHistoryStore` und `useHistoryCapture`). Reine, seiteneffektfreie Funktionen – leicht
 * testbar und ohne Abhängigkeit zu Vue/Pinia.
 */

/** Erzeugt aus einem Originalnamen einen Griffschrift-Ergebnisnamen. */
export function griffschriftName(filename: string): string {
    const base = filename.replace(/\.(mxl|xml|musicxml|mid|midi|pdf)$/i, '');
    return `${base}_griffschrift.musicxml`;
}

/**
 * Ergebnis-Download-Name mit gewünschter Endung, z. B. `<base>_griffschrift.pdf`. Leitet den Basis-
 * namen aus dem Originalnamen ab (Endung entfernt); ohne Namen Fallback `griffschrift`.
 */
export function griffschriftDownloadName(filename: string | null | undefined, ext: string): string {
    const raw = (filename ?? '').trim();
    const base = raw
        ? raw.replace(/\.(mxl|xml|musicxml|mid|midi|pdf|png)$/i, '')
        : 'griffschrift';
    return `${base}_griffschrift.${ext}`;
}

/**
 * Normalisiert MusicXML für die Duplikat-Erkennung: entfernt BOM, die XML-Deklaration
 * (encoding/version-Varianten) und insignifikanten Whitespace **zwischen** Tags, vereinheitlicht
 * Zeilenenden. Whitespace innerhalb von Text-/Lyric-Knoten bleibt erhalten. So gelten zwei nur
 * unterschiedlich serialisierte (eingerückte) Fassungen derselben Partitur als identisch.
 */
export function normalizeXml(xml: string): string {
    return xml
        .replace(/^﻿/, '')
        .replace(/<\?xml[^>]*\?>/i, '')
        .replace(/\r\n/g, '\n')
        .replace(/>\s+</g, '><')
        .trim();
}

/**
 * SHA-256 als Hex-String – für die Duplikat-Erkennung. MusicXML wird vor dem Hashen normalisiert
 * (siehe {@link normalizeXml}); Binärinhalte (MIDI/MXL/PDF) werden byte-genau gehasht.
 * `null` bei fehlender Crypto-API.
 */
export async function hashBlob(blob: Blob): Promise<string | null> {
    try {
        if (typeof crypto === 'undefined' || !crypto.subtle) return null;
        const buf = await blob.arrayBuffer();
        const text = new TextDecoder().decode(buf);
        const data = text.trimStart().startsWith('<')
            ? new TextEncoder().encode(normalizeXml(text))
            : buf;
        const digest = await crypto.subtle.digest('SHA-256', data);
        return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch {
        return null;
    }
}
